import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/safeClient';
import { useAuth } from './AuthContext';
import { isUuid } from '@/lib/validation';
import { 
  LIGHT_COLUMNS, 
  PROFILE_COLUMNS, 
  fetchLikeCounts,
  type ListingLight,
  type Profile 
} from '@/services/listings';

// Re-export for backward compatibility
export type { Profile, ListingLight };
export type Listing = ListingLight;

interface RealtimeState {
  listings: Map<string, ListingLight>;
  likes: Map<string, number>;
  profiles: Map<string, Profile>;
  isReady: boolean;
  pendingLikeDelta: Map<string, number>;
  ignoreNextLikeEvent: Map<string, number>;
}

interface RealtimeContextValue extends RealtimeState {
  refreshListing: (id: string) => Promise<void>;
  refreshProfile: (id: string) => Promise<void>;
  getLikeCount: (listingId: string) => number;
  adjustLikeCount: (listingId: string, delta: 1 | -1) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<RealtimeState>({
    listings: new Map(),
    likes: new Map(),
    profiles: new Map(),
    isReady: false,
    pendingLikeDelta: new Map(),
    ignoreNextLikeEvent: new Map(),
  });

  // Initial data fetch - OPTIMIZED: using lightweight columns
  const LISTINGS_LIMIT = 100;

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Fetch listings with seller profiles - OPTIMIZED: only essential columns
        const { data: listingsData, error: listingsError } = await supabase
          .from('listings')
          .select(`
            ${LIGHT_COLUMNS},
            seller:profiles!seller_id(${PROFILE_COLUMNS})
          `)
          .order('created_at', { ascending: false })
          .limit(LISTINGS_LIMIT);

        if (listingsError) {
          console.error('RealtimeProvider: Error fetching listings:', listingsError);
          setState((prev) => ({ ...prev, isReady: true }));
          return;
        }

        if (listingsData) {
          const listingsMap = new Map<string, ListingLight>();
          const profilesMap = new Map<string, Profile>();
          const listingIds: string[] = [];

          // Build listings and profiles maps
          for (const listing of listingsData) {
            // Skip listings where seller profile couldn't be loaded (due to RLS)
            if (!listing.seller) {
              continue;
            }
            
            listingsMap.set(listing.id, listing as ListingLight);
            profilesMap.set(listing.seller.id, listing.seller as Profile);
            listingIds.push(listing.id);
          }

          // Fetch all like counts in a SINGLE query (no more N+1!)
          const likesMap = await fetchLikeCounts(listingIds);

          setState({
            listings: listingsMap,
            profiles: profilesMap,
            likes: likesMap,
            isReady: true,
            pendingLikeDelta: new Map(),
            ignoreNextLikeEvent: new Map(),
          });
        } else {
          setState((prev) => ({ ...prev, isReady: true }));
        }
      } catch (error) {
        console.error('RealtimeProvider: Unexpected error:', error);
        setState((prev) => ({ ...prev, isReady: true }));
      }
    };

    loadInitialData();
  }, [user?.id]);

  // Real-time subscriptions
  useEffect(() => {
    if (!state.isReady) return;

    // 1️⃣ Listings channel
    const listingsChannel = supabase
      .channel('realtime:listings')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'listings',
        },
        async (payload) => {
          const newListing = payload.new as any;
          
          // Skip fetching profile if seller_id is not a valid UUID (e.g., demo listings)
          if (!newListing.seller_id || !isUuid(newListing.seller_id)) {
            return;
          }
          
          // Fetch seller profile
          const { data: seller } = await supabase
            .from('profiles')
            .select('id, name, avatar_url, county, verified, trust_score')
            .eq('id', newListing.seller_id)
            .single();

          if (seller) {
            setState((prev) => {
              const newListings = new Map(prev.listings);
              const newProfiles = new Map(prev.profiles);
              const newLikes = new Map(prev.likes);
              
              newListings.set(newListing.id, { ...newListing, seller });
              newProfiles.set(seller.id, seller);
              newLikes.set(newListing.id, 0);
              
              return { ...prev, listings: newListings, profiles: newProfiles, likes: newLikes };
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'listings',
        },
        (payload) => {
          const updatedListing = payload.new as any;
          
          setState((prev) => {
            const newListings = new Map(prev.listings);
            const existing = newListings.get(updatedListing.id);
            
            if (existing) {
              newListings.set(updatedListing.id, {
                ...existing,
                ...updatedListing,
                seller: existing.seller, // Keep existing seller
              });
            }
            
            return { ...prev, listings: newListings };
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'listings',
        },
        (payload) => {
          const deletedId = payload.old.id;
          
          setState((prev) => {
            const newListings = new Map(prev.listings);
            const newLikes = new Map(prev.likes);
            
            newListings.delete(deletedId);
            newLikes.delete(deletedId);
            
            return { ...prev, listings: newListings, likes: newLikes };
          });
        }
      )
      .subscribe();

    // 2️⃣ Likes channel - with optimistic update handling
    const likesChannel = supabase
      .channel('realtime:likes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'likes',
          filter: 'target_type=eq.listing',
        },
        (payload) => {
          const targetId = payload.new.target_id;
          
          setState((prev) => {
            const newLikes = new Map(prev.likes);
            const newIgnore = new Map(prev.ignoreNextLikeEvent);
            const newPending = new Map(prev.pendingLikeDelta);
            
            // Check if we should ignore this event (it's our optimistic update)
            const ignoreCount = newIgnore.get(targetId) || 0;
            if (ignoreCount > 0) {
              newIgnore.set(targetId, ignoreCount - 1);
              // Confirmed our optimistic update: move pending delta into base count
              const pendingDelta = newPending.get(targetId) || 0;
              if (pendingDelta > 0) {
                newPending.set(targetId, pendingDelta - 1);
                // Increment base count since the server confirmed the like
                const currentCount = newLikes.get(targetId) || 0;
                newLikes.set(targetId, currentCount + 1);
              }
              return { 
                ...prev, 
                likes: newLikes,
                ignoreNextLikeEvent: newIgnore,
                pendingLikeDelta: newPending 
              };
            }
            
            // This is from another user, update the base count
            const currentCount = newLikes.get(targetId) || 0;
            const newCount = currentCount + 1;
            newLikes.set(targetId, newCount);
            
            return { ...prev, likes: newLikes };
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'likes',
          filter: 'target_type=eq.listing',
        },
        (payload) => {
          const targetId = payload.old.target_id;
          
          setState((prev) => {
            const newLikes = new Map(prev.likes);
            const newIgnore = new Map(prev.ignoreNextLikeEvent);
            const newPending = new Map(prev.pendingLikeDelta);
            
            // Check if we should ignore this event (it's our optimistic update)
            const ignoreCount = newIgnore.get(targetId) || 0;
            if (ignoreCount > 0) {
              newIgnore.set(targetId, ignoreCount - 1);
              // Confirmed our optimistic unlike: move pending delta into base count
              const pendingDelta = newPending.get(targetId) || 0;
              if (pendingDelta < 0) {
                newPending.set(targetId, pendingDelta + 1);
                // Decrement base count since the server confirmed the unlike
                const currentCount = newLikes.get(targetId) || 1;
                newLikes.set(targetId, Math.max(0, currentCount - 1));
              }
              return { 
                ...prev, 
                likes: newLikes,
                ignoreNextLikeEvent: newIgnore,
                pendingLikeDelta: newPending 
              };
            }
            
            // This is from another user, update the base count
            const currentCount = newLikes.get(targetId) || 1;
            const newCount = Math.max(0, currentCount - 1);
            newLikes.set(targetId, newCount);
            
            return { ...prev, likes: newLikes };
          });
        }
      )
      .subscribe();

    // 3️⃣ Profiles channel - 최적화: 연결된 listings만 selective update
    const profilesChannel = supabase
      .channel('realtime:profiles')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
        },
        (payload) => {
          const updatedProfile = payload.new as Profile;
          
          setState((prev) => {
            const newProfiles = new Map(prev.profiles);
            const newListings = new Map(prev.listings);
            
            // Update profile
            newProfiles.set(updatedProfile.id, updatedProfile);
            
            // Update all listings from this seller (selective update)
            for (const [listingId, listing] of newListings.entries()) {
              if (listing.seller_id === updatedProfile.id) {
                newListings.set(listingId, {
                  ...listing,
                  seller: updatedProfile,
                });
              }
            }
            
            return { ...prev, profiles: newProfiles, listings: newListings };
          });
        }
      )
      .subscribe();

    // 3️⃣ Cleanup 시 removeAllChannels() 호출
    return () => {
      supabase.removeChannel(listingsChannel);
      supabase.removeChannel(likesChannel);
      supabase.removeChannel(profilesChannel);
    };
  }, [state.isReady]);

  // Helper functions - OPTIMIZED: use lightweight columns
  const refreshListing = async (id: string) => {
    const { data } = await supabase
      .from('listings')
      .select(`
        ${LIGHT_COLUMNS},
        seller:profiles!seller_id(${PROFILE_COLUMNS})
      `)
      .eq('id', id)
      .single();

    if (data) {
      setState((prev) => {
        const newListings = new Map(prev.listings);
        newListings.set(id, data as ListingLight);
        
        if (data.seller) {
          const newProfiles = new Map(prev.profiles);
          newProfiles.set(data.seller.id, data.seller as Profile);
          return { ...prev, listings: newListings, profiles: newProfiles };
        }
        
        return { ...prev, listings: newListings };
      });
    }
  };

  const refreshProfile = async (id: string) => {
    // Skip if not a valid UUID
    if (!id || !isUuid(id)) {
      return;
    }
    
    const { data } = await supabase
      .from('profiles')
      .select('id, name, avatar_url, county, verified, trust_score')
      .eq('id', id)
      .single();

    if (data) {
      setState((prev) => {
        const newProfiles = new Map(prev.profiles);
        newProfiles.set(id, data as Profile);
        return { ...prev, profiles: newProfiles };
      });
    }
  };

  // Get current like count (base + pending delta)
  const getLikeCount = (listingId: string): number => {
    const baseLikes = state.likes.get(listingId) || 0;
    const pendingDelta = state.pendingLikeDelta.get(listingId) || 0;
    const total = Math.max(0, baseLikes + pendingDelta);
    return total;
  };

  // Adjust like count optimistically
  const adjustLikeCount = (listingId: string, delta: 1 | -1) => {
    setState((prev) => {
      const newPending = new Map(prev.pendingLikeDelta);
      const newIgnore = new Map(prev.ignoreNextLikeEvent);
      
      const currentPending = newPending.get(listingId) || 0;
      newPending.set(listingId, currentPending + delta);
      
      const currentIgnore = newIgnore.get(listingId) || 0;
      newIgnore.set(listingId, currentIgnore + 1);
      
      return {
        ...prev,
        pendingLikeDelta: newPending,
        ignoreNextLikeEvent: newIgnore,
      };
    });
  };

  return (
    <RealtimeContext.Provider 
      value={{
        ...state,
        refreshListing,
        refreshProfile,
        getLikeCount,
        adjustLikeCount,
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within RealtimeProvider');
  }
  return context;
}
